/*
 * TSS Skin Quiz, UI Controller (v2, copy-driven + Q2 branching)
 * ------------------------------------------------------------
 * All user-visible copy lives in tss-quiz-copy.js (window.TSSQuizCopy).
 * Persona rules live in tss-quiz-personas.js (window.TSSQuizEngine).
 * This file is UI + state machine only, don't put strings here.
 *
 * Flow:
 *   greeting -> photo_intro (the fork)
 *     photo path:     photo -> photo_confirm -> [q1 if unread] -> [q3 if acne] -> q6 -> compute
 *     questions path: q1 -> q2_main -> (q2_active, q2_texture) -> q2b -> q3? -> q4? -> q6 -> compute
 *   photo_intro only appears when the vision Worker passes its health check.
 *   Every photo failure falls back to the questions path, never to compute.
 *   (Q5 makeup and Q7 tried-before are currently hidden. When re-enabled, re-insert them.)
 *
 * Events emitted on document:
 *   tss-quiz:start, tss-quiz:answer, tss-quiz:complete, tss-quiz:open, tss-quiz:close
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'tss_quiz_session_v1';
  var COMPLETE_COOKIE = 'tss_quiz_complete';
  var RESULT_PAGE = '/pages/your-routine';
  var TYPING_MIN = 550;         // P2-12: vary typing 550–900ms for more human feel
  var TYPING_MAX = 900;
  var AUTO_ADVANCE_MS = 15000;

  // P2-11: haptic feedback on touch-capable devices
  function haptic() {
    try { if (navigator.vibrate) navigator.vibrate(12); } catch (e) {}
  }

  // Grab copy file, fallback gracefully if missing (logs a warning)
  var C = window.TSSQuizCopy;
  if (!C) {
    console.warn('[TSS Quiz] tss-quiz-copy.js not loaded. Quiz will fail.');
    return;
  }

  // ---- State --------------------------------------------------------------
  var state = load() || freshState();
  var ui = {};
  var currentStep = null;
  var drillQueue = []; // queue of drill steps to process after q2_main

  function freshState() {
    return {
      session_id: uuid(),
      started_at: null,
      updated_at: null,
      completed: false,
      entry_source: null,
      answers: {
        skin_type: null,
        concern_buckets: [],   // NEW: what user picked in q2_main (e.g., ['active_acne','marks'])
        concerns: [],          // specific sub-options (unchanged, engine reads this)
        free_text: '',
        acne_freq: null,
        triggers: [],
        makeup: null,
        sensitivity: null,
        tried_before: [],
        phone: '',
        email: ''
      },
      persona: null
    };
  }

  function save() {
    state.updated_at = new Date().toISOString();
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) {}
  }
  function load() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
  }
  function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c){
      var r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 3 | 8)).toString(16);
    });
  }
  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent('tss-quiz:' + name, { detail: detail || {} }));
  }
  function setCookie(name, value, days) {
    var d = new Date(); d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + value + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  // ---- Open / Close -------------------------------------------------------
  function open(source) {
    if (!ui.chat) return;
    ui.chat.classList.add('tss-open');
    if (!state.started_at) {
      state.started_at = new Date().toISOString();
      state.entry_source = source || 'bubble';
      save();
      emit('start', { session_id: state.session_id, source: state.entry_source });
      startFlow();
    } else if (state.completed) {
      ui.body.innerHTML = '';
      showReturningUser();
    } else {
      restoreFlow();
    }
    emit('open', { source: source });
  }

  function close() {
    if (!ui.chat) return;
    ui.chat.classList.remove('tss-open');
    // Invalidate any in-flight photo read. Without this, a read that lands
    // after the user has closed the widget would carry on through to compute
    // and force a page redirect nobody asked for.
    photoRun++;
    emit('close');
  }

  function showReturningUser() {
    if (ui.stepLabel) ui.stepLabel.style.display = 'none';
    botSay(C.returning[0], function () {
      botSay(C.returning[1], function () {
        var container = document.createElement('div');
        container.className = 'tss-msg tss-msg--user';
        container.style.maxWidth = '100%';

        var viewBtn = document.createElement('a');
        viewBtn.href = RESULT_PAGE;
        viewBtn.className = 'tss-done-btn';
        viewBtn.style.textDecoration = 'none';
        viewBtn.style.display = 'inline-block';
        viewBtn.textContent = C.returning_cta_view;
        container.appendChild(viewBtn);

        var retake = document.createElement('button');
        retake.className = 'tss-skip-btn';
        retake.textContent = C.returning_cta_retake;
        retake.onclick = function () {
          // The stored thumbnail is a photo of their face. Clear it with the session.
          try { if (window.TSSQuizVision) window.TSSQuizVision.clearThumbs(); } catch (e) {}
          state = freshState();
          state.started_at = new Date().toISOString();
          state.entry_source = 'retake_from_bubble';
          save();
          try { document.cookie = COMPLETE_COOKIE + '=; Max-Age=0; path=/'; } catch(e){}
          ui.body.innerHTML = '';
          if (ui.stepLabel) ui.stepLabel.style.display = '';
          emit('start', { session_id: state.session_id, source: state.entry_source });
          startFlow();
        };
        container.appendChild(retake);
        ui.body.appendChild(container);
        scrollBottom();
      });
    });
  }

  // ---- Flow machine -------------------------------------------------------
  function startFlow() {
    ui.body.innerHTML = '';
    currentStep = 'greeting';
    renderStep();
  }

  function restoreFlow() {
    ui.body.innerHTML = '';
    // P1-7: show "Start over" chip at top of resumed session
    showStartOverChip();
    var a = state.answers;
    // v2: the photo fork is the first thing asked, so route on it first.
    if (!a.path && !a.skin_type) currentStep = 'greeting';
    else if (a.path === 'photo') currentStep = photoPathStep(a);
    else if (!a.skin_type) currentStep = 'q1';
    else if (!a.concern_buckets || !a.concern_buckets.length) currentStep = 'q2_main';
    else if (!a.q2_drill_done) currentStep = 'q2_drills';
    else if (a.free_text === '' && !a.q2b_skipped) currentStep = 'q2b';
    else if (needsQ3() && !a.acne_freq) currentStep = 'q3';
    else if (needsQ4() && !a.triggers_done) currentStep = 'q4';
    // Q5 (makeup) + Q7 (tried-before) are hidden for now, flow skips straight from q4 → q6 → contact
    else if (!a.sensitivity_done) currentStep = 'q6';
    // Contact step hidden for now. To re-enable, uncomment the line below.
    // else if (!a.contact_done) currentStep = 'contact';
    else currentStep = 'compute';
    renderStep();
  }

  function showStartOverChip() {
    if (!ui.body) return;
    var chip = document.createElement('div');
    chip.style.cssText = 'text-align:center; padding:8px 0; font-size:12px;';
    chip.innerHTML = '<span style="color:var(--tss-muted);">Resuming where you left off. </span>' +
                     '<a href="#" data-tss-startover style="color:var(--tss-primary); text-decoration:underline; font-weight:500;">Start over</a>';
    chip.querySelector('[data-tss-startover]').onclick = function (e) {
      e.preventDefault();
      if (confirm('Start the quiz over? Your current answers will be cleared.')) {
        // The stored thumbnail is a photo of their face. Clear it with the session.
        try { if (window.TSSQuizVision) window.TSSQuizVision.clearThumbs(); } catch (e) {}
        state = freshState();
        state.started_at = new Date().toISOString();
        state.entry_source = 'restart_from_mid';
        save();
        ui.body.innerHTML = '';
        emit('start', { session_id: state.session_id, source: state.entry_source });
        startFlow();
      }
    };
    ui.body.appendChild(chip);
  }

  function needsQ4() {
    // Q4 (triggers) is only relevant for users with active breakouts or texture concerns.
    // People with only marks, rosacea, fungal acne, irritation, or maintenance skip it.
    var buckets = state.answers.concern_buckets || [];
    return buckets.indexOf('active_acne') !== -1 || buckets.indexOf('texture') !== -1;
  }

  function needsQ3() {
    return (state.answers.concern_buckets || []).indexOf('active_acne') !== -1;
  }

  function updateStep(step, total) {
    if (ui.stepLabel) {
      ui.stepLabel.style.display = '';
      ui.stepLabel.textContent = 'Step ' + step + ' of ' + total;
    }
    if (ui.progressBar) {
      var pct = Math.max(0, Math.min(100, (step / total) * 100));
      ui.progressBar.style.width = pct + '%';
      ui.progressBar.style.display = '';
      var wrap = ui.progressBar.parentNode;
      if (wrap) wrap.style.display = '';
    }
  }

  // Wrapped so a throw in ANY step cannot leave the customer staring at a dead
  // widget. Photo steps fall back to the questions, everything else to compute,
  // because by then there are enough answers to build a routine.
  function renderStep() {
    try {
      renderStepInner();
    } catch (e) {
      console.error('[TSS Quiz] step "' + currentStep + '" failed:', e);
      var photoSteps = ['photo_intro', 'photo', 'photo_confirm', 'reconcile'];
      var fallback = (photoSteps.indexOf(currentStep) !== -1) ? 'q1' : 'compute';
      if (fallback === 'q1') {
        state.answers.path = 'questions';
        state.answers.photo_done = true;
        state.answers.photo_skipped = true;
        try { save(); } catch (e2) {}
      }
      if (currentStep === fallback) return;   // never loop
      currentStep = fallback;
      try { renderStepInner(); } catch (e3) {
        console.error('[TSS Quiz] fallback step also failed:', e3);
      }
    }
  }

  function renderStepInner() {
    switch (currentStep) {
      case 'greeting':   return stepGreeting();
      case 'q1':         return stepQ1();
      case 'q2_main':    return stepQ2Main();
      case 'q2_drills':  return processDrillQueue();
      case 'q2b':        return stepQ2b();
      case 'q3':         return stepQ3();
      case 'q4':         return stepQ4();
      case 'q5':         return stepQ5();
      case 'q6':         return stepQ6();
      case 'photo_intro':   return stepPhotoIntro();
      case 'photo':         return stepPhoto();
      case 'photo_confirm': return stepPhotoConfirm();
      case 'reconcile':     return stepReconcile();
      case 'q7':         return stepQ7();
      case 'contact':    return stepContact();
      case 'compute':    return stepCompute();
    }
  }

  // ---- Step implementations ----------------------------------------------
  function stepGreeting() {
    updateStep(1, 5);
    botSay(C.greeting[0], function () {
      botSay(C.greeting[1], function () {
        currentStep = 'photo_intro'; renderStep();
      });
    });
  }

  function stepQ1() {
    updateStep(1, 5);
    emit('step_view', { step: 'q1', session_id: state.session_id });
    var q = C.questions.q1;
    botSay(q.prompt, function () {
      renderOptions(q.options, { multi: false, helper: q.helper, usePhotos: true }, function (val) {
        state.answers.skin_type = val; save();
        userSay(labelFor(q.options, val));
        emit('answer', { q: 'q1', value: val });
        ackRandom('q1', val, function () { currentStep = onPhotoPath() ? nextAfterPhoto() : 'q2_main'; renderStep(); });
      });
    });
  }

  function stepQ2Main() {
    updateStep(2, 5);
    emit('step_view', { step: 'q2_main', session_id: state.session_id });
    var q = C.questions.q2_main;
    botSay(q.prompt, function () {
      renderOptions(q.options, { multi: true, helper: q.helper, usePhotos: true }, function (buckets) {
        state.answers.concern_buckets = buckets;

        // Build drill queue based on which buckets need drilling
        drillQueue = [];
        buckets.forEach(function (bucketValue) {
          var optDef = q.options.find(function (o) { return o.value === bucketValue; });
          if (!optDef) return;
          if (optDef.drill) {
            drillQueue.push(optDef.drill);
          } else if (optDef.adds) {
            // Non-drilled buckets: auto-add their canonical concern values
            optDef.adds.forEach(function (v) {
              if (state.answers.concerns.indexOf(v) === -1) state.answers.concerns.push(v);
            });
          }
        });
        save();

        userSay(buckets.map(function(v){ return labelFor(q.options, v); }).join(', '));
        emit('answer', { q: 'q2_main', value: buckets });

        // If drills needed, process them. Otherwise send Q2 response + move to q2b.
        if (drillQueue.length) {
          currentStep = 'q2_drills';
          renderStep();
        } else {
          finishQ2();
        }
      });
    });
  }

  function processDrillQueue() {
    updateStep(2, 5);
    if (!drillQueue.length) return finishQ2();
    var next = drillQueue.shift();
    var q = C.questions[next];
    if (!q) return finishQ2();
    emit('step_view', { step: next, session_id: state.session_id });
    botSay(q.prompt, function () {
      renderOptions(q.options, { multi: true, usePhotos: true, helper: q.helper }, function (vals) {
        vals.forEach(function (v) {
          if (state.answers.concerns.indexOf(v) === -1) state.answers.concerns.push(v);
        });
        save();
        userSay(vals.map(function(v){ return labelFor(q.options, v); }).join(', '));
        emit('answer', { q: next, value: vals });
        // Continue queue or finish Q2
        if (drillQueue.length) processDrillQueue();
        else finishQ2();
      });
    });
  }

  function finishQ2() {
    state.answers.q2_drill_done = true;
    // Q2b (free-text) is hidden for now. Mark as skipped so restoreFlow doesn't land on it.
    state.answers.q2b_skipped = true;
    state.answers.free_text = '';
    save();
    var ackText = buildQ2Response();
    botSay(ackText, function () {
      currentStep = needsQ3() ? 'q3' : (needsQ4() ? 'q4' : 'q6');
      renderStep();
    });
  }

  function buildQ2Response() {
    var buckets = state.answers.concern_buckets || [];
    var has = function (b) { return buckets.indexOf(b) !== -1; };
    var rules = {
      has_fungal:         has('fungal_acne'),
      has_rosacea:        has('rosacea'),
      has_irritation:     has('irritation'),
      has_acne_and_marks: has('active_acne') && has('marks'),
      has_acne:           has('active_acne'),
      has_marks:          has('marks'),
      default:            true
    };
    var match = C.q2_responses.find(function (r) { return rules[r.when]; });
    return match ? match.text : 'Got it.';
  }

  function stepQ2b() {
    updateStep(3, 5);
    emit('step_view', { step: 'q2b', session_id: state.session_id });
    var q = C.questions.q2b;
    botSay(q.prompt, function () {
      renderFreeText(q.placeholder, function (txt, skipped) {
        state.answers.free_text = skipped ? '' : (txt || '');
        state.answers.q2b_skipped = true; save();
        if (txt) userSay(txt);
        emit('answer', { q: 'q2b', value: state.answers.free_text, skipped: skipped });
        botSay(txt ? C.q2b_response_filled : C.q2b_response_empty, function () {
          currentStep = needsQ3() ? 'q3' : (needsQ4() ? 'q4' : 'q6'); renderStep();
        });
      });
    });
  }

  function stepQ3() {
    updateStep(4, 5);
    emit('step_view', { step: 'q3', session_id: state.session_id });
    var q = C.questions.q3;
    botSay(q.prompt, function () {
      renderOptions(q.options, { multi: false, helper: q.helper }, function (val) {
        state.answers.acne_freq = val; save();
        userSay(labelFor(q.options, val));
        emit('answer', { q: 'q3', value: val });
        ackRandom('q3', val, function () { currentStep = onPhotoPath() ? nextAfterPhoto() : (needsQ4() ? 'q4' : 'q6'); renderStep(); });
      });
    });
  }

  function stepQ4() {
    updateStep(4, 5);
    emit('step_view', { step: 'q4', session_id: state.session_id });
    var q = C.questions.q4;
    botSay(q.prompt, function () {
      renderOptions(q.options, { multi: true, optional: true, autoAdvance: true }, function (vals, skipped) {
        state.answers.triggers = skipped ? [] : vals;
        state.answers.triggers_done = true; save();
        if (!skipped && vals.length) userSay(vals.map(function(v){return labelFor(q.options, v);}).join(', '));
        emit('answer', { q: 'q4', value: state.answers.triggers, skipped: skipped });
        currentStep = 'q6'; renderStep();
      });
    });
  }

  function stepQ5() {
    updateStep(5, 5);
    var q = C.questions.q5;
    botSay(q.prompt, function () {
      renderOptions(q.options, { multi: false }, function (val) {
        state.answers.makeup = val; save();
        userSay(labelFor(q.options, val));
        emit('answer', { q: 'q5', value: val });
        ackRandom('q5', val, function () { currentStep = 'q6'; renderStep(); });
      });
    });
  }

  function stepQ6() {
    updateStep(5, 5);
    emit('step_view', { step: 'q6', session_id: state.session_id });
    var q = C.questions.q6;
    botSay(q6Prompt(q), function () {
      renderOptions(q.options, { multi: false, optional: true, autoAdvance: true }, function (val, skipped) {
        state.answers.sensitivity = skipped ? null : val;
        state.answers.sensitivity_done = true; save();
        if (!skipped && val) userSay(labelFor(q.options, val));
        emit('answer', { q: 'q6', value: state.answers.sensitivity, skipped: skipped });
        // Contact step (phone + email) hidden for now. To re-enable, change
        // 'compute' back to 'contact' and restore the contact line in the
        // resume logic above. Was: currentStep = 'contact';
        currentStep = 'compute'; renderStep();
      });
    });
  }

  // On the photo path the redness row is gone from the check card (redness
  // is colour, not reactivity), so when the photo clearly saw it, Q6 says so
  // and the customer decides.
  function q6Prompt(q) {
    if (!onPhotoPath() || !q.prompt_photo_redness) return q.prompt;
    var V = window.TSSQuizVision;
    try {
      var c = V && V.confirmableReads ? V.confirmableReads(state.answers.photo_read) : null;
      if (c && c.redness) return q.prompt_photo_redness;
    } catch (e) {}
    return q.prompt;
  }

  function stepQ7() {
    updateStep(5, 5);
    var q = C.questions.q7;
    botSay(q.prompt, function () {
      renderOptions(q.options, { multi: true, optional: true, autoAdvance: true }, function (vals, skipped) {
        state.answers.tried_before = skipped ? [] : vals;
        state.answers.tried_done = true; save();
        if (!skipped && vals.length) userSay(vals.map(function(v){return labelFor(q.options, v);}).join(', '));
        emit('answer', { q: 'q7', value: state.answers.tried_before, skipped: skipped });
        currentStep = 'contact'; renderStep();
      });
    });
  }

  function stepContact() {
    updateStep(5, 5);
    emit('step_view', { step: 'contact', session_id: state.session_id });
    var q = C.questions.contact;
    botSay(q.prompt, function () {
      renderContact(q, function (phone, email, skipped) {
        state.answers.phone = skipped ? '' : (phone || '');
        state.answers.email = skipped ? '' : (email || '');
        state.answers.contact_done = true; save();
        if (!skipped && (phone || email)) {
          userSay((phone ? phone : '') + (phone && email ? ' · ' : '') + (email || ''));
        }
        emit('answer', { q: 'contact', phone: state.answers.phone, email: state.answers.email, skipped: skipped });
        currentStep = 'compute'; renderStep();
      });
    });
  }

  // ---- Photo step (v2) -----------------------------------------------------
  // Optional. Every path through here ends in a full routine, including every
  // failure path. Skipping leaves the result identical to v1.
  //
  // Sub-flow: offer -> consent -> capture -> read -> (reconcile) -> compute
  // All the image work and all the rules live in tss-quiz-vision.js.

  var MAX_PHOTO_ATTEMPTS = 3; // first go plus two retakes, then move on

  // Bumped whenever the widget closes or the photo step restarts. Any async
  // photo callback compares against it and bails if it has gone stale, so a
  // read that lands late can never drive the flow or redirect the page.
  var photoRun = 0;

  // Checks the WHOLE vision API, not just that the object exists.
  //
  // This is deliberately paranoid. tss-quiz.js and tss-quiz-vision.js are two
  // separate Shopify assets uploaded by hand, so they can easily end up out of
  // sync (it has happened twice). An older vision.js missing one function used
  // to throw and kill the quiz outright. Now any mismatch just means the photo
  // is not offered and the quiz runs exactly as v1.
  var VISION_API = ['isEnabled', 'preflight', 'processFile', 'analyse', 'maxImages',
                    'buildReconcile', 'applyFindings', 'confirmableReads',
                    'findConflicts', 'applyChoices', 'saveThumb', 'loadThumb',
                    'clearThumbs'];

  var visionApiWarned = false;

  function visionReady() {
    var V = window.TSSQuizVision;
    if (!V) return false;
    for (var i = 0; i < VISION_API.length; i++) {
      if (typeof V[VISION_API[i]] !== 'function') {
        if (!visionApiWarned) {
          visionApiWarned = true;
          console.warn('[TSS Quiz] tss-quiz-vision.js is missing ' +
            VISION_API[i] + '(), so it is an older version than tss-quiz.js. ' +
            'The photo step is disabled. Re-upload assets/tss-quiz-vision.js ' +
            'from quiz-build-v2 to fix it. The quiz itself is unaffected.');
        }
        return false;
      }
    }
    if (!V.isEnabled()) return false;
    return !!(window.fetch && window.FileReader && window.Promise);
  }

  function photoFinish() {
    state.answers.photo_done = true;
    save();
    currentStep = 'compute';
    renderStep();
  }

  function photoSkip(at) {
    emit('photo_skip', { session_id: state.session_id, at: at || 'offer' });
    photoFallback(null, 'q1');
  }

  // ---- Path helpers --------------------------------------------------------

  function onPhotoPath() {
    return state.answers.path === 'photo';
  }

  // What a photo genuinely cannot see, so what we still have to ask. This is
  // the whole reason the photo path is short but not lossy.
  //
  // acne_freq is the one that matters most: the engine branches
  // persistent/frequent vs periodic vs rare on it, and with no answer it falls
  // through to the RARE routine ("RCF is SOS only"). Skipping it would quietly
  // under-treat every persistent-acne customer who used the photo.
  function nextAfterPhoto() {
    var a = state.answers;
    if (!a.skin_type) return 'q1';                       // photo could not read it
    if (needsQ3() && !a.acne_freq) return 'q3';          // a photo is one day
    if (needsQ4() && !a.triggers_done) return 'q4';      // a photo can't see causes
    if (!a.sensitivity_done) return 'q6';                // self-report only
    return 'compute';
  }

  function photoPathStep(a) {
    if (!a.photo_done) return 'photo';
    if (!a.photo_confirmed) return 'photo_confirm';
    return nextAfterPhoto();
  }

  // Every failure on the photo path lands here: drop to the normal questions
  // rather than to compute, so nobody gets a routine built from nothing.
  function photoFallback(message, after) {
    state.answers.path = 'questions';
    state.answers.photo_done = true;
    state.answers.photo_skipped = true;
    state.answers.photo_read = null;
    save();
    var go = function () { currentStep = (after || 'q1'); renderStep(); };
    if (message) botSay(message, go); else go();
  }

  // ---- The fork, asked before anything else --------------------------------

  function stepPhotoIntro() {
    updateStep(1, 5);

    // No photo offer at all unless the backend actually answers. A broken or
    // misconfigured Worker must never show a customer a photo button.
    if (!visionReady() || !C.questions || !C.questions.photo_intro ||
        !C.questions.photo || !C.questions.photo_confirm) {
      state.answers.path = 'questions';
      save();
      currentStep = 'q1'; renderStep();
      return;
    }

    var V = window.TSSQuizVision;
    var pf = (V && typeof V.preflight === 'function')
      ? V.preflight()
      : Promise.resolve(false);

    pf['catch'](function () { return false; }).then(function (ok) {
      if (!ok) {
        state.answers.path = 'questions';
        save();
        emit('photo_unavailable', { session_id: state.session_id });
        currentStep = 'q1'; renderStep();
        return;
      }

      emit('step_view', { step: 'photo_intro', session_id: state.session_id });
      var q = C.questions.photo_intro;
      botSay(q.prompt, function () {
        botSay(q.sub, function () {
          renderOptions(q.options, { multi: false }, function (val) {
            userSay(labelFor(q.options, val));
            state.answers.path = (val === 'photo') ? 'photo' : 'questions';
            save();
            emit('answer', { q: 'photo_intro', value: val, skipped: false });
            emit(val === 'photo' ? 'photo_opt_in' : 'photo_skip', {
              session_id: state.session_id, at: 'intro'
            });
            currentStep = (val === 'photo') ? 'photo' : 'q1';
            renderStep();
          });
        });
      });
    });
  }

  function stepPhoto() {
    updateStep(2, 5);

    // Kill switch, a browser too old for any of this, OR a copy file without
    // the photo block (which happens if tss-quiz.js is uploaded to Shopify but
    // tss-quiz-copy.js is not). In every case, fall back to the questions.
    if (!visionReady() || !C.questions || !C.questions.photo) {
      photoFallback(null, 'q1');
      return;
    }

    // Old thumbnails from previous sessions on this device. Nothing here needs
    // them, and they are a face photo, so clear them before adding another.
    try { window.TSSQuizVision.clearThumbs(); } catch (e) {}

    photoRun++;   // orphan any read still in flight from an earlier pass

    // Clear anything left from an abandoned earlier attempt, so a half
    // finished read can never leak onto the result page.
    state.answers.photo_read = null;
    state.answers.photo_reconcile = null;
    state.answers.photo_reconcile_variant = null;
    state.answers.photo_attempts = 0;

    emit('step_view', { step: 'photo', session_id: state.session_id });
    var q = C.questions.photo;

    // ONE typing animation, not three. The first build chained
    // prompt -> sub -> consent_title, which meant roughly nine seconds of
    // typing dots before anyone could tick a box.
    botSay(q.prompt + ' ' + q.sub, function () {
      renderPhotoPrivacy(q.privacy);
      photoConsent();
    });
  }

  function photoConsent() {
    var q = C.questions.photo;
    // No extra bubble here. The title sits inside the panel instead, so the
    // checkboxes appear immediately rather than after another typing delay.
    renderPhotoConsent(q, function (agreed) {
      if (!agreed) {
        userSay(q.echo_skipped);
        photoSkip('consent');
        return;
      }
      state.answers.photo_consent = true;
      save();
      emit('photo_consent_given', { session_id: state.session_id });
      photoCapture();
    });
  }

  // `kept` carries photos that were fine through a retake, so a good side
  // photo is not thrown away because the front one needs doing again.
  function photoCapture(kept) {
    var q = C.questions.photo;
    renderPhotoCapture(q, kept || {}, function (slots) {
      if (!slots) {
        userSay(q.echo_skipped);
        photoSkip('capture');
        return;
      }
      photoProcess(slots);
    });
  }

  var PHOTO_VIEWS = ['front', 'left', 'right'];

  // slots: { front: processed, left?: processed, right?: processed }, where
  // processed is what TSSQuizVision.processFile() returned. Every photo in it
  // has already passed the on-device checks, tile by tile.
  function photoProcess(slots) {
    var q = C.questions.photo;
    var V = window.TSSQuizVision;
    var views = PHOTO_VIEWS.filter(function (v) { return !!slots[v]; });

    state.answers.photo_attempts = (state.answers.photo_attempts || 0) + 1;
    save();
    emit('photo_captured', {
      session_id: state.session_id,
      attempt: state.answers.photo_attempts,
      views: views.join(',')
    });
    userSay(views.length > 1
      ? (q.echo_added_multi || q.echo_added).replace('{n}', views.length)
      : q.echo_added);

    // Small thumbnail of the FRONT photo, kept in this browser only, so the
    // result page can show it back. Never uploaded anywhere.
    V.saveThumb(state.session_id, slots.front.thumb);

    var reading = renderPhotoReading(q, views.length);
    var run = photoRun;
    function stale() {
      if (run === photoRun) return false;
      reading.stop();
      return true;
    }

    V.analyse({
      images: views.map(function (v) {
        return { view: v, b64: slots[v].b64, mime: slots[v].mime };
      }),
      answers: {
        skin_type: state.answers.skin_type,
        concerns: state.answers.concern_buckets,
        acne_freq: state.answers.acne_freq,
        sensitivity: state.answers.sensitivity
      }
    }).then(function (out) {
      if (stale()) return;
      reading.stop();

      if (!out.ok) {
        emit('photo_analysis_fail', {
          session_id: state.session_id,
          error: out.error, latency_ms: out.latency_ms, views: views.join(',')
        });
        photoBail(q.failed);
        return;
      }

      var an = out.analysis;
      state.answers.photo_model = out.model || null;

      if (an.usable !== true) {
        state.answers.photo_usable = false;
        save();
        emit('photo_validation_fail', {
          session_id: state.session_id, reason: an.reason,
          tier: 'model', attempt: state.answers.photo_attempts
        });
        photoRetake(an.reason, photosToKeep(an, slots));
        return;
      }

      var used = (an.views || []).filter(function (v) { return v.used; })
        .map(function (v) { return v.view; });
      state.answers.photo_read = an;
      state.answers.photo_usable = true;
      state.answers.photo_views = used.length ? used : ['front'];
      save();
      emit('photo_analysis_success', {
        session_id: state.session_id,
        latency_ms: out.latency_ms,
        refer: !!an.refer_to_human,
        views_sent: views.length,
        views_used: state.answers.photo_views.length
      });
      photoAfterRead(an);
    })['catch'](function (err) {
      if (stale()) return;
      reading.stop();
      emit('photo_analysis_fail', {
        session_id: state.session_id,
        error: String((err && err.message) || err)
      });
      photoBail(C.questions.photo.failed);
    });
  }

  // After the Worker turns the read down, which photos are worth keeping for
  // the retake. The front decides usability, so it always goes. A side photo
  // stays unless the model said it was unusable too. Nothing is kept when the
  // reason is about the person rather than the photo.
  function photosToKeep(an, slots) {
    var keep = {};
    if (an.reason === 'appears_under_18' || an.reason === 'not_a_face') return keep;
    var views = an.views || [];
    ['left', 'right'].forEach(function (v) {
      if (!slots[v]) return;
      var entry = views.filter(function (x) { return x.view === v; })[0];
      if (!entry || entry.used !== false) keep[v] = slots[v];
    });
    return keep;
  }

  function photoRetake(reason, kept) {
    var q = C.questions.photo;
    if ((state.answers.photo_attempts || 0) >= MAX_PHOTO_ATTEMPTS) {
      emit('photo_partial_read', {
        session_id: state.session_id,
        attempts: state.answers.photo_attempts
      });
      photoBail(q.give_up);
      return;
    }
    emit('photo_retake', {
      session_id: state.session_id,
      attempt: state.answers.photo_attempts, reason: reason
    });
    botSay(q.retake[reason] || q.retake.fallback, function () {
      photoCapture(kept);
    });
  }

  // Give up on the photo but never on the customer. Falls back to the normal
  // questions, so they still get a routine built on real answers.
  function photoBail(message) {
    photoFallback(message, 'q1');
  }

  function mergeUnique(a, b) {
    var out = (a || []).slice();
    (b || []).forEach(function (x) { if (out.indexOf(x) === -1) out.push(x); });
    return out;
  }

  // On the photo-first path there is nothing to reconcile against yet, because
  // they have not answered anything. The confirm screen does that job instead,
  // and does it better: they see the whole read and can correct all of it.
  // Nothing from the photo is written into their answers here. It is applied
  // only when they tap "Looks right". Applying it up front meant a rejected
  // or "didn't tell us much" read could leave photo-derived concerns behind,
  // and because Q2 appends to answers.concerns they survived into the
  // routine and showed up on the result page as things "you didn't mention".
  function photoAfterRead(analysis) {
    state.answers.photo_done = true;
    save();
    currentStep = 'photo_confirm';
    renderStep();
  }

  // Returns true if findings were applied, false if the vision module is gone.
  function photoApply(analysis, reconcileValue) {
    var V = window.TSSQuizVision;
    if (!V) return false;   // vision.js rolled back mid-session

    var changed = V.applyFindings(analysis, state.answers, reconcileValue, { seed: onPhotoPath() });

    // MERGE, never overwrite. applyFindings is idempotent, so a second pass
    // reports an empty added/removed list. Assigning that over a first pass
    // would silently erase the result page's disclosure of what changed.
    var prev = state.answers.vision_changes || { added: [], removed: [] };
    state.answers.vision_changes = {
      added:   mergeUnique(prev.added, changed.added),
      removed: mergeUnique(prev.removed, changed.removed),
      skin_type_set: changed.skin_type_set || prev.skin_type_set || null,
      removal_outcome: changed.removal_outcome || prev.removal_outcome || null
    };
    changed = state.answers.vision_changes;
    state.answers.photo_done = true;
    save();
    if (changed.added.length || changed.removed.length || changed.skin_type_set) {
      emit('findings_applied', {
        session_id: state.session_id,
        added: changed.added.join(','),
        removed: changed.removed.join(','),
        skin_type_set: changed.skin_type_set || ''
      });
    }
    return true;
  }

  // ---- Check what the photo read ------------------------------------------
  // One card with every concern row on it. Where the photo was confident
  // (medium or high) the row is pre-set and they can change it. Where it was
  // not, the row is left blank and they answer it themselves.
  //
  // The first version showed only the confident rows and treated everything
  // else as "not there". A real bearded selfie came back with one confident
  // row ("Marks: nothing obvious"), the customer tapped "Looks right", and was
  // given a maintenance routine without ever being asked about breakouts.
  // Couldn't tell must never turn into doesn't have.
  function stepPhotoConfirm() {
    updateStep(3, 5);

    var read = state.answers.photo_read;
    var q = C.questions && C.questions.photo_confirm;

    // Nothing usable to check, or the vision file is gone. Use the questions.
    if (!read || read.usable !== true || !q || !visionReady()) {
      state.answers.photo_confirmed = true;
      state.answers.path = 'questions';
      save();
      currentStep = 'q1'; renderStep();
      return;
    }

    emit('step_view', { step: 'photo_confirm', session_id: state.session_id });

    var rows = buildConfirmRows(read, q);

    // Not one concern read with any confidence. A card of blank rows would
    // just be a worse Q2, so say so and use the real questions. The read is
    // kept for the result page.
    var seen = rows.filter(function (r) { return r.concern && r.preset; });
    if (!seen.length) {
      state.answers.photo_confirmed = true;
      state.answers.path = 'questions';
      state.answers.photo_source = 'thin';
      save();
      emit('photo_thin_read', { session_id: state.session_id });
      botSay(q.thin, function () { currentStep = 'q1'; renderStep(); });
      return;
    }

    var anyOpen = rows.some(function (r) { return r.section === 'open'; });
    var multiPhoto = (read.views || []).filter(function (v) { return v.used; }).length > 1;
    var prompt = anyOpen
      ? ((multiPhoto && q.prompt_mixed_multi) || q.prompt_mixed || q.prompt)
      : (q.prompt_all_seen || q.prompt);

    botSay(prompt, function () {
      renderConfirmCard(rows, q, droppedNotice(read, q), function (result) {
        state.answers.photo_confirmed = true;

        if (result.divert) {
          // They want to answer for themselves. Their answers win outright:
          // nothing from the photo is applied. The read is kept only so the
          // result page can show where the photo saw it differently.
          userSay(q.echo_divert);
          state.answers.path = 'questions';
          state.answers.photo_source = 'diverted';
          state.answers.photo_choices = null;
          state.answers.photo_edits = [];
          state.answers.photo_answered = [];
          state.answers.concerns = [];
          state.answers.concern_buckets = [];
          state.answers.skin_type = null;
          state.answers.vision_changes = { added: [], removed: [], skin_type_set: null, removal_outcome: null };
          save();
          emit('answer', { q: 'photo_confirm', value: 'divert', skipped: false });
          emit('photo_confirm_diverted', { session_id: state.session_id });
          botSay(q.divert_note, function () { currentStep = 'q1'; renderStep(); });
          return;
        }

        // 'confirmed' only when they changed nothing AND nothing was blank,
        // because the result page then says the routine is built on the photo.
        var touched = result.changed.length > 0 || result.answered.length > 0;
        userSay(touched ? q.echo_edited : q.echo_accept);

        state.answers.photo_choices = result.choices;
        state.answers.photo_edits = result.changed;
        state.answers.photo_answered = result.answered;
        state.answers.photo_source = touched ? 'edited' : 'confirmed';

        var changed = window.TSSQuizVision.applyChoices(result.choices, state.answers);
        state.answers.vision_changes = {
          added: changed.added, removed: [],
          skin_type_set: changed.skin_type_set || null, removal_outcome: null
        };
        save();

        emit('answer', { q: 'photo_confirm', value: touched ? 'edited' : 'accept', skipped: false });
        emit('photo_confirm_accepted', {
          session_id: state.session_id,
          changed: result.changed.join(','),
          answered: result.answered.join(',')
        });
        emit('findings_applied', {
          session_id: state.session_id,
          added: changed.added.join(','),
          removed: '',
          skin_type_set: changed.skin_type_set || ''
        });

        currentStep = nextAfterPhoto();
        renderStep();
      });
    });
  }

  // Every concern row, always, plus skin type. Built from confirmableReads(),
  // the one rule for which reads count as seen:
  //   section 'seen'  the photo was confident. Pre-filled, and shows what the
  //                   photo saw, so they can see why and change it.
  //   section 'open'  the photo could not show it clearly. Blank, they answer.
  //   concern: true   rows that decide the routine and count towards "the
  //                   photo saw something". Skin type does not.
  //
  // No redness row. Redness is colour, not reactivity, and Q6 asks about
  // reactivity a moment later anyway. Asking both confused people.
  function buildConfirmRows(read, q) {
    var c = window.TSSQuizVision.confirmableReads(read);
    var R = q.rows || {};
    var rows = [];

    var sk = R.skin_type || {};
    var skinChoices = ['oily', 'combination', 'dry', 'normal'].map(function (t) {
      return { value: t, label: q.skin_types[t] || t };
    });
    if (c.skin_type) {
      rows.push({
        key: 'skin_type', concern: false, section: 'seen',
        question: sk.question || q.labels.skin_type,
        photo: (q.seen_skin && q.seen_skin[c.skin_type.value]) || c.skin_type.value,
        help: '',
        preset: c.skin_type.value,
        choices: skinChoices
      });
    } else {
      // Asked here rather than as Q1 afterwards, which opened with "Let's
      // start with the basics" several steps into the flow.
      rows.push({
        key: 'skin_type', concern: false, section: 'open',
        question: sk.question || q.labels.skin_type,
        photo: null,
        help: sk.help || '',
        preset: null,
        choices: skinChoices.concat([{ value: 'not_sure', label: q.skin_types.not_sure || 'Not sure' }])
      });
    }

    ['active_acne', 'marks', 'texture'].forEach(function (k) {
      var r = c[k];
      var d = R[k] || {};
      var seen = !!r;
      // The timeframe matters most when the photo says "nothing obvious":
      // one photo is one day, and someone who breaks out monthly must not
      // just accept "No".
      var help = seen ? (d.timeframe || '') : [d.help, d.timeframe].filter(Boolean).join(' ');
      rows.push({
        key: k, concern: true, section: seen ? 'seen' : 'open',
        question: d.question || q.labels[k],
        photo: seen ? ((q.seen_values && q.seen_values[r.value]) || r.value) : null,
        help: help,
        preset: seen ? (r.value === 'none' ? 'no' : 'yes') : null,
        choices: [
          { value: 'yes', label: d.yes || q.yes || 'Yes' },
          { value: 'no',  label: d.no  || q.no  || 'No' }
        ]
      });
    });

    return rows;
  }

  // The card itself. Calls done() once, with either { divert: true } or
  // { choices, changed, answered }:
  //   choices   final value per row key
  //   changed   rows the photo pre-set that they switched
  //   answered  rows the photo couldn't see, so they answered
  function renderConfirmCard(rows, q, notice, done) {
    var read = state.answers.photo_read || {};
    var multi = (read.views || []).filter(function (v) { return v.used; }).length > 1;

    var wrap = document.createElement('div');
    wrap.className = 'tss-msg tss-msg--user tss-photo__wrap';
    wrap.style.maxWidth = '100%';

    var box = document.createElement('div');
    box.className = 'tss-freetext tss-photo__verify';

    if (notice) {
      var nb = document.createElement('div');
      nb.className = 'tss-photo__verify-notice';
      nb.textContent = notice;
      box.appendChild(nb);
    }

    var choices = {};
    rows.forEach(function (r) { if (r.preset) choices[r.key] = r.preset; });

    var finished = false;
    var chips = [];
    var readLines = {};

    var cta = document.createElement('button');
    cta.type = 'button';
    // Not sticky: the answers span the full width, and a floating button
    // sat on top of the right-hand one.
    cta.className = 'tss-done-btn tss-done-btn--inline';

    var divert = document.createElement('button');
    divert.type = 'button';
    divert.className = 'tss-skip-btn';
    divert.textContent = q.divert_label || '';

    var hint = document.createElement('div');
    hint.className = 'tss-photo__verify-hint';

    var prefix = (multi && q.seen_prefix_multi) ? q.seen_prefix_multi : (q.seen_prefix || '');

    function readText(r) {
      var t = (prefix ? prefix + ' ' : '') + r.photo + '.';
      if (r.preset && choices[r.key] && choices[r.key] !== r.preset && q.changed_note) {
        t += ' ' + q.changed_note;
      }
      return t;
    }

    function refresh() {
      var blank = 0, touched = false;
      rows.forEach(function (r) {
        if (!choices[r.key]) blank++;
        else if (choices[r.key] !== r.preset) touched = true;
        if (readLines[r.key]) readLines[r.key].textContent = readText(r);
      });
      var anyOpen = rows.some(function (r) { return !r.preset; });
      cta.disabled = blank > 0;
      cta.textContent = (blank || touched || anyOpen)
        ? (q.cta_continue || 'Continue')
        : (q.cta_accept || 'Looks right');
      if (blank) {
        hint.textContent = blank === 1
          ? (q.need_answer_one || q.need_answer || '')
          : (q.need_answer_many || q.need_answer || '').replace('{n}', blank);
        hint.className = 'tss-photo__verify-hint tss-photo__verify-hint--todo';
      } else {
        hint.textContent = q.note || '';
        hint.className = 'tss-photo__verify-hint';
      }
    }

    function section(title, sub, open) {
      var s = document.createElement('div');
      s.className = 'tss-photo__verify-section' + (open ? ' tss-photo__verify-section--open' : '');
      var t = document.createElement('span');
      t.className = 'tss-photo__verify-eyebrow';
      t.textContent = title;
      s.appendChild(t);
      if (sub) {
        var u = document.createElement('span');
        u.className = 'tss-photo__verify-sub';
        u.textContent = sub;
        s.appendChild(u);
      }
      box.appendChild(s);
    }

    function renderRow(r) {
      var row = document.createElement('div');
      row.className = 'tss-photo__verify-row' + (r.preset ? '' : ' tss-photo__verify-row--open');

      var head = document.createElement('div');
      head.className = 'tss-photo__verify-head';
      var l = document.createElement('span');
      l.className = 'tss-photo__verify-label';
      l.textContent = r.question;
      head.appendChild(l);
      if (r.photo) {
        var p = document.createElement('span');
        p.className = 'tss-photo__verify-read';
        readLines[r.key] = p;
        head.appendChild(p);
      }
      if (r.help) {
        var h = document.createElement('span');
        h.className = 'tss-photo__verify-help';
        h.textContent = r.help;
        head.appendChild(h);
      }

      var group = document.createElement('div');
      group.className = 'tss-photo__verify-choices' +
        (r.choices.length === 2 ? ' tss-photo__verify-choices--pair' : '');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', r.question);

      var own = [];
      r.choices.forEach(function (ch) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'tss-option tss-photo__verify-chip';
        b.textContent = ch.label;
        var paint = function () {
          var on = choices[r.key] === ch.value;
          if (on) b.classList.add('tss-selected'); else b.classList.remove('tss-selected');
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        };
        b.onclick = function () {
          if (finished) return;
          haptic();
          choices[r.key] = ch.value;
          for (var i = 0; i < own.length; i++) own[i]();
          row.className = 'tss-photo__verify-row';
          refresh();
        };
        own.push(paint);
        chips.push(b);
        group.appendChild(b);
        paint();
      });

      row.appendChild(head);
      row.appendChild(group);
      box.appendChild(row);
    }

    var seenRows = rows.filter(function (r) { return r.section === 'seen'; });
    var openRows = rows.filter(function (r) { return r.section === 'open'; });

    if (seenRows.length) {
      section(multi ? (q.section_seen_multi || q.section_seen) : q.section_seen,
              q.section_seen_sub, false);
      seenRows.forEach(renderRow);
    }
    if (openRows.length) {
      section(q.section_open,
              multi ? (q.section_open_sub_multi || q.section_open_sub) : q.section_open_sub, true);
      openRows.forEach(renderRow);
    }

    box.appendChild(hint);
    wrap.appendChild(box);
    wrap.appendChild(cta);
    wrap.appendChild(divert);
    ui.body.appendChild(wrap);
    refresh();
    scrollPromptToTop();

    // The card stays in the chat as a record of what they agreed to, but
    // frozen, so nothing on it looks tappable afterwards.
    function finish(result) {
      if (finished) return;
      finished = true;
      for (var i = 0; i < chips.length; i++) chips[i].disabled = true;
      if (cta.parentNode) cta.parentNode.removeChild(cta);
      if (divert.parentNode) divert.parentNode.removeChild(divert);
      if (hint.parentNode) hint.parentNode.removeChild(hint);
      box.className += ' tss-photo__verify--done';
      done(result);
    }

    cta.onclick = function () {
      if (cta.disabled || finished) return;
      haptic();
      var changed = [], answered = [], out = {};
      rows.forEach(function (r) {
        out[r.key] = choices[r.key];
        if (!r.preset) answered.push(r.key);
        else if (choices[r.key] !== r.preset) changed.push(r.key);
      });
      finish({ divert: false, choices: out, changed: changed, answered: answered });
    };
    divert.onclick = function () {
      if (finished) return;
      finish({ divert: true });
    };
  }

  // "We couldn't use your left side photo..." when the model dropped one.
  function droppedNotice(read, q) {
    var names = q.view_names || {};
    var dropped = (read.views || []).filter(function (v) { return v.used === false; })
      .map(function (v) { return names[v.view] || v.view; });
    if (!dropped.length) return '';
    var tpl = dropped.length > 1 ? q.views_dropped_many : q.views_dropped_one;
    return (tpl || '').replace('{views}', dropped.join(' and '));
  }

  // The one reconcile question. Only ever asked about active acne, and the
  // answer is the ONLY thing allowed to drop a concern the user picked.
  // NOTE: unreachable for new sessions since the photo moved to the front of
  // the flow. Kept so a session saved mid-reconcile still resolves cleanly.
  function stepReconcile() {
    updateStep(5, 5);

    // A session can resume straight into this step. By then the kill switch
    // may be on, vision.js may have been rolled back, or the copy block may be
    // missing. Any of those and we go straight to the routine rather than
    // asking a question we cannot act on.
    if (!visionReady() || !state.answers.photo_read ||
        !C.questions || !C.questions.photo || !C.questions.photo.reconcile) {
      photoFinish();
      return;
    }

    emit('step_view', { step: 'reconcile', session_id: state.session_id });

    var q = C.questions.photo;
    var variant = state.answers.photo_reconcile_variant || 'clear';
    var block = q.reconcile[variant] || q.reconcile.clear;

    botSay(block.prompt, function () {
      renderOptions(block.options, { multi: false }, function (val) {
        userSay(labelFor(block.options, val));
        state.answers.photo_reconcile = val;
        save();
        emit('answer', { q: 'reconcile', value: val, skipped: false });
        emit('findings_reconciled', {
          session_id: state.session_id, variant: variant, value: val
        });
        photoApply(state.answers.photo_read, val);
        photoFinish();
      });
    });
  }

  // ---- Photo renderers -----------------------------------------------------

  function renderPhotoPrivacy(text) {
    var el = document.createElement('div');
    el.className = 'tss-photo__privacy';
    el.textContent = text;
    ui.body.appendChild(el);
    scrollBottom();
  }

  function renderPhotoConsent(q, done) {
    var wrap = document.createElement('div');
    wrap.className = 'tss-msg tss-msg--user tss-photo__wrap';

    var box = document.createElement('div');
    box.className = 'tss-freetext tss-photo__consent';

    if (q.consent_title) {
      var head = document.createElement('div');
      head.className = 'tss-photo__title';
      head.textContent = q.consent_title;
      box.appendChild(head);
    }

    var boxes = [];
    [q.consent_analyse, q.consent_age].forEach(function (label, i) {
      var row = document.createElement('label');
      row.className = 'tss-photo__check';
      var cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.id = 'tss-photo-consent-' + i;
      var span = document.createElement('span');
      span.textContent = label;
      row.appendChild(cb);
      row.appendChild(span);
      box.appendChild(row);
      boxes.push(cb);
    });

    var cta = document.createElement('button');
    cta.className = 'tss-done-btn';
    cta.textContent = q.consent_cta;
    cta.disabled = true;

    var skip = document.createElement('button');
    skip.className = 'tss-skip-btn';
    skip.textContent = q.consent_back;

    function refresh() {
      cta.disabled = !boxes.every(function (b) { return b.checked; });
    }
    boxes.forEach(function (b) { b.addEventListener('change', refresh); });

    function cleanup() {
      cta.disabled = true;
      skip.disabled = true;
      boxes.forEach(function (b) { b.disabled = true; });
    }

    cta.addEventListener('click', function () {
      if (cta.disabled) return;
      cleanup();
      done(true);
    });
    skip.addEventListener('click', function () {
      cleanup();
      done(false);
    });

    wrap.appendChild(box);
    wrap.appendChild(cta);
    wrap.appendChild(skip);
    ui.body.appendChild(wrap);
    scrollBottom();
  }

  // The capture panel: tips, then one tile per view. Front is needed, the
  // sides are optional and only shown when the Worker can take them.
  //
  // Each tile is its own file input with no `capture` attribute, so the phone
  // offers its usual "take a photo or choose one" sheet. Every photo is run
  // through the on-device checks the moment it is picked, and a bad one just
  // resets that tile with the fix, instead of costing a whole attempt.
  //
  // done(slots) once with the photos, or done(null) if they skip.
  function renderPhotoCapture(q, kept, done) {
    var V = window.TSSQuizVision;
    var views = (V.maxImages() > 1) ? PHOTO_VIEWS.slice() : ['front'];
    var labels = q.capture_views || {};
    var run = photoRun;

    var slots = {};
    views.forEach(function (v) { if (kept[v]) slots[v] = kept[v]; });
    var busy = {};
    var settled = false;

    var wrap = document.createElement('div');
    wrap.className = 'tss-msg tss-msg--user tss-photo__wrap';
    wrap.style.maxWidth = '100%';

    var box = document.createElement('div');
    box.className = 'tss-freetext tss-photo__capture';

    var title = document.createElement('div');
    title.className = 'tss-photo__title';
    title.textContent = q.capture_title;
    box.appendChild(title);

    var list = document.createElement('ul');
    list.className = 'tss-photo__tips';
    (q.capture_tips || []).forEach(function (t) {
      var li = document.createElement('li');
      li.textContent = t;
      list.appendChild(li);
    });
    box.appendChild(list);

    var grid = document.createElement('div');
    grid.className = 'tss-photo__slots' + (views.length === 1 ? ' tss-photo__slots--one' : '');
    box.appendChild(grid);

    var msg = document.createElement('div');
    msg.className = 'tss-photo__slot-msg';
    box.appendChild(msg);

    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'tss-done-btn tss-done-btn--inline';

    var skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'tss-skip-btn';
    skip.textContent = q.capture_skip;

    var tiles = {}, camInputs = {}, fileInputs = {};
    var active = null;    // the tile the camera / gallery choices are for

    // The action row: "Take a photo" and "Choose from gallery" for the
    // selected tile. The camera needs its OWN input with capture="user". A
    // plain file input used to show a camera-or-gallery choice, but newer
    // Android phones send it straight to the system photo picker, which has
    // no camera at all. That is how the first tile version lost the camera.
    var actions = document.createElement('div');
    actions.className = 'tss-photo__actions';
    actions.hidden = true;
    var actTitle = document.createElement('div');
    actTitle.className = 'tss-photo__actions-title';
    var actHow = document.createElement('div');
    actHow.className = 'tss-photo__actions-how';
    var actBtns = document.createElement('div');
    actBtns.className = 'tss-photo__actions-btns';
    var takeBtn = document.createElement('button');
    takeBtn.type = 'button';
    takeBtn.className = 'tss-photo__action tss-photo__action--primary';
    takeBtn.textContent = q.capture_take || q.capture_cta;
    var pickBtn = document.createElement('button');
    pickBtn.type = 'button';
    pickBtn.className = 'tss-photo__action';
    pickBtn.textContent = q.capture_pick || q.capture_upload;
    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'tss-photo__actions-cancel';
    cancelBtn.textContent = q.capture_cancel || 'Cancel';
    actBtns.appendChild(takeBtn);
    actBtns.appendChild(pickBtn);
    actions.appendChild(actTitle);
    actions.appendChild(actHow);
    actions.appendChild(actBtns);
    actions.appendChild(cancelBtn);
    box.insertBefore(actions, msg);

    function viewLabel(v) { return (labels[v] && labels[v].label) || v; }

    function paint(v) {
      var t = tiles[v];
      var has = !!slots[v];
      t.className = 'tss-photo__slot' +
        (has ? ' tss-photo__slot--filled' : '') +
        (busy[v] ? ' tss-photo__slot--busy' : '') +
        (v === 'front' ? ' tss-photo__slot--needed' : '') +
        (active === v ? ' tss-photo__slot--active' : '');
      t.innerHTML = '';
      if (has) {
        var img = document.createElement('img');
        img.className = 'tss-photo__slot-img';
        img.alt = '';
        img.src = slots[v].thumb;
        t.appendChild(img);
        var tick = document.createElement('span');
        tick.className = 'tss-photo__slot-tick';
        tick.setAttribute('aria-hidden', 'true');
        t.appendChild(tick);
      } else {
        var plus = document.createElement('span');
        plus.className = 'tss-photo__slot-plus';
        plus.setAttribute('aria-hidden', 'true');
        t.appendChild(plus);
      }
      var l = document.createElement('span');
      l.className = 'tss-photo__slot-label';
      l.textContent = viewLabel(v);
      t.appendChild(l);
      var sub = document.createElement('span');
      sub.className = 'tss-photo__slot-sub';
      sub.textContent = busy[v] ? (q.capture_checking || '')
        : has ? (q.capture_change || '')
        : ((labels[v] && labels[v].sub) || '');
      t.appendChild(sub);
      t.setAttribute('aria-label', viewLabel(v) + ', ' + sub.textContent);
      t.setAttribute('aria-pressed', active === v ? 'true' : 'false');
      t.disabled = settled;
    }

    function openActions(v) {
      active = v;
      actTitle.textContent = (q.capture_action_title || '{view}').replace('{view}', viewLabel(v));
      actHow.textContent = (labels[v] && labels[v].how) || '';
      // The front is needed, so there is nothing to cancel back to until it
      // is in. Everywhere else they can close the choices again.
      cancelBtn.hidden = (v === 'front' && !slots.front);
      actions.hidden = false;
      views.forEach(paint);
    }

    function closeActions() {
      active = null;
      actions.hidden = true;
      views.forEach(paint);
    }

    function refresh(errorText) {
      var n = views.filter(function (v) { return !!slots[v]; }).length;
      var anyBusy = views.some(function (v) { return !!busy[v]; });
      cta.disabled = settled || !slots.front || anyBusy;
      // Count the front even while it is missing (after a retake the sides
      // are kept), so the button says what will actually be sent.
      var shown = slots.front ? n : n + 1;
      cta.textContent = shown > 1
        ? (q.capture_read_many || '').replace('{n}', shown)
        : (q.capture_read_one || q.capture_cta);
      if (errorText) {
        msg.textContent = errorText;
        msg.className = 'tss-photo__slot-msg tss-photo__slot-msg--error';
        return;
      }
      msg.className = 'tss-photo__slot-msg';
      if (views.length === 1) msg.textContent = q.capture_hint_single || '';
      else if (!slots.front && n > 0) msg.textContent = q.capture_hint_front_again || q.capture_hint_start || '';
      else if (!slots.front) msg.textContent = q.capture_hint_start || '';
      else if (n < views.length) msg.textContent = q.capture_hint_more || '';
      else msg.textContent = q.capture_hint_all || '';
    }

    function handleFile(v, file) {
      if (!file || settled) return;
      busy[v] = true;
      paint(v); refresh();
      V.processFile(file).then(function (res) {
        if (run !== photoRun || settled) return;
        busy[v] = false;
        if (!res.ok) {
          delete slots[v];
          emit('photo_validation_fail', {
            session_id: state.session_id, reason: res.reason,
            tier: 'client', view: v
          });
          // Keep the choices open on the same tile so they can go again.
          openActions(v);
          var line = C.questions.photo.retake[res.reason] || C.questions.photo.retake.fallback;
          refresh((q.capture_slot_error || '{message}')
            .replace('{view}', viewLabel(v)).replace('{message}', line));
          return;
        }
        slots[v] = res;
        closeActions();
        refresh();
      });
    }

    function makeInput(v, kind) {
      var input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      if (kind === 'camera') input.setAttribute('capture', 'user');
      input.className = 'tss-photo__file';
      input.setAttribute('data-view', v);
      input.setAttribute('data-kind', kind);
      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        input.value = '';          // so picking the same file again still fires
        handleFile(v, file);
      });
      box.appendChild(input);
      return input;
    }

    views.forEach(function (v) {
      camInputs[v] = makeInput(v, 'camera');
      fileInputs[v] = makeInput(v, 'gallery');

      var tile = document.createElement('button');
      tile.type = 'button';
      tiles[v] = tile;
      tile.addEventListener('click', function () {
        if (settled || busy[v]) return;
        haptic();
        if (active === v && !(v === 'front' && !slots.front)) closeActions();
        else openActions(v);
      });
      grid.appendChild(tile);
      paint(v);
    });

    takeBtn.addEventListener('click', function () {
      if (settled || !active || busy[active]) return;
      haptic();
      camInputs[active].click();
    });
    pickBtn.addEventListener('click', function () {
      if (settled || !active || busy[active]) return;
      fileInputs[active].click();
    });
    cancelBtn.addEventListener('click', function () {
      if (settled) return;
      closeActions();
      refresh();
    });

    function finish(result) {
      if (settled) return;
      settled = true;
      // Retakes leave earlier panels in the chat history. Disable everything
      // here, the hidden inputs included, so a stale panel can never fire.
      views.forEach(function (v) {
        camInputs[v].disabled = true;
        fileInputs[v].disabled = true;
      });
      takeBtn.disabled = true;
      pickBtn.disabled = true;
      cancelBtn.disabled = true;
      actions.hidden = true;
      active = null;
      views.forEach(paint);
      cta.disabled = true;
      skip.disabled = true;
      done(result);
    }

    cta.addEventListener('click', function () {
      if (cta.disabled) return;
      var out = {};
      views.forEach(function (v) { if (slots[v]) out[v] = slots[v]; });
      finish(out);
    });
    skip.addEventListener('click', function () { finish(null); });

    wrap.appendChild(box);
    wrap.appendChild(cta);
    wrap.appendChild(skip);
    ui.body.appendChild(wrap);
    // Front first: its camera and gallery choices are showing from the start,
    // so a one-photo customer has exactly the old two taps.
    if (!slots.front) openActions('front');
    refresh();
    scrollBottom();
  }

  // Reuses the existing three-dot typing indicator rather than inventing a
  // second spinner. Returns a handle so the caller can stop it.
  function renderPhotoReading(q, count) {
    var wrap = document.createElement('div');
    wrap.className = 'tss-msg tss-msg--bot tss-photo__reading';
    wrap.innerHTML =
      '<div class="tss-msg__bubble">' +
        '<span class="tss-photo__reading-text"></span>' +
        '<span class="tss-typing tss-photo__reading-dots">' +
          '<span></span><span></span><span></span>' +
        '</span>' +
      '</div>';
    var textEl = wrap.querySelector('.tss-photo__reading-text');
    textEl.textContent = (count > 1 && q.reading_multi) ? q.reading_multi : q.reading;
    ui.body.appendChild(wrap);
    scrollBottom();

    var steps = q.reading_steps || [];
    var i = 0;
    var timer = steps.length ? setInterval(function () {
      textEl.textContent = steps[i % steps.length];
      i++;
    }, 3500) : null;

    return {
      stop: function () {
        if (timer) clearInterval(timer);
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      }
    };
  }

  function stepCompute() {
    updateStep(5, 5);
    var lines = C.computing.slice();
    function next() {
      if (!lines.length) return finishAndRedirect();
      botSay(lines.shift(), next);
    }
    next();
  }

  function finishAndRedirect() {
    var result = window.TSSQuizEngine.compute(state.answers);
    state.persona = result;
    state.completed = true; save();
    setCookie(COMPLETE_COOKIE, '1', 30);
    emit('complete', { session_id: state.session_id, persona: result });

    // Kick off AI copy generation in parallel with the computing transition.
    // Runs async. If it finishes before redirect, the PDP reads from cache
    // and renders AI copy instantly. If it doesn't, the PDP renders template
    // copy first and the AI result populates on the next visit or when
    // generate() is re-called there.
    if (window.TSSQuizAI && typeof window.TSSQuizAI.generate === 'function') {
      try { window.TSSQuizAI.generate(state); } catch (e) { /* non-fatal */ }
    }

    typing(function () {
      botSay(C.computing_final, function () {
        setTimeout(function () {
          window.location.href = RESULT_PAGE + '#s=' + state.session_id;
        }, 400);
      });
    });
  }

  // ---- Renderers ----------------------------------------------------------
  function botSay(text, done) {
    typing(function () {
      var el = document.createElement('div');
      el.className = 'tss-msg tss-msg--bot';
      el.innerHTML = '<div class="tss-msg__bubble"></div>';
      el.querySelector('.tss-msg__bubble').textContent = text;
      ui.body.appendChild(el);
      scrollBottom();
      if (done) setTimeout(done, 120);
    });
  }

  function userSay(text) {
    var el = document.createElement('div');
    el.className = 'tss-msg tss-msg--user';
    el.innerHTML = '<div class="tss-msg__bubble"></div>';
    el.querySelector('.tss-msg__bubble').textContent = text;
    ui.body.appendChild(el);
    scrollBottom();
  }

  function typing(done) {
    var el = document.createElement('div');
    el.className = 'tss-typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    ui.body.appendChild(el);
    scrollBottom();
    var delay = TYPING_MIN + Math.floor(Math.random() * (TYPING_MAX - TYPING_MIN));
    setTimeout(function () { el.remove(); done(); }, delay);
  }

  function renderOptions(opts, cfg, onChoose) {
    // If any option has an image, render as card grid. Else use pill buttons.
    var anyImage = (cfg.usePhotos !== false) && opts.some(function (o) { return !!o.image; });
    var wrap = document.createElement('div');
    wrap.className = anyImage ? 'tss-cards' : 'tss-options';
    var chosen = [];
    var autoTimer = null;

    opts.forEach(function (o) {
      var btn;
      if (anyImage) {
        btn = document.createElement('button');
        btn.className = 'tss-card' + (cfg.multi ? ' tss-card--multi' : '');
        var innerHTML = '';
        if (o.image) innerHTML += '<div class="tss-card__img"><img src="' + esc(o.image) + '" alt="" loading="lazy"/></div>';
        else innerHTML += '<div class="tss-card__img tss-card__img--none"></div>';
        innerHTML += '<div class="tss-card__label">' + esc(o.label) + '</div>';
        if (o.hint) innerHTML += '<div class="tss-card__hint">' + esc(o.hint) + '</div>';
        btn.innerHTML = innerHTML;
      } else {
        btn = document.createElement('button');
        btn.className = 'tss-option' + (cfg.multi ? ' tss-option--multi' : '');
        btn.textContent = o.label;
      }
      btn.onclick = function () {
        cancelAuto();
        haptic(); // P2-11
        if (cfg.multi) {
          var i = chosen.indexOf(o.value);
          if (i === -1) { chosen.push(o.value); btn.classList.add('tss-selected'); }
          else { chosen.splice(i, 1); btn.classList.remove('tss-selected'); }
          if (doneBtn) doneBtn.disabled = chosen.length === 0;
        } else {
          btn.classList.add('tss-selected');
          wrap.querySelectorAll('.tss-option, .tss-card').forEach(function(b){ if (b!==btn) b.disabled = true; });
          setTimeout(function () { cleanup(); onChoose(o.value); }, 200);
        }
      };
      wrap.appendChild(btn);
    });

    var doneBtn = null;
    if (cfg.multi) {
      doneBtn = document.createElement('button');
      doneBtn.className = 'tss-done-btn';
      doneBtn.textContent = 'Done';
      doneBtn.disabled = true;
      doneBtn.onclick = function () { cancelAuto(); cleanup(); onChoose(chosen); };
    }

    var helperLink = null;
    if (cfg.helper) {
      helperLink = document.createElement('span');
      helperLink.className = 'tss-msg__hint';
      helperLink.textContent = 'Help me decide';
      helperLink.onclick = function () { showHelper(cfg.helper); };
    }

    var skipBtn = null;
    if (cfg.optional) {
      skipBtn = document.createElement('button');
      skipBtn.className = 'tss-skip-btn';
      skipBtn.textContent = 'Skip this';
      skipBtn.onclick = function () { cancelAuto(); cleanup(); onChoose(cfg.multi ? [] : null, true); };
    }

    var container = document.createElement('div');
    container.className = 'tss-msg tss-msg--user';
    container.style.maxWidth = '100%';
    if (helperLink) container.appendChild(helperLink); // P1-9: moved above options
    container.appendChild(wrap);
    if (doneBtn) container.appendChild(doneBtn);
    if (skipBtn) container.appendChild(skipBtn);
    ui.body.appendChild(container);
    // Instead of jumping to the absolute bottom (which hides the question prompt
    // when the option list is tall, e.g. Q2 with 6 cards), scroll so the most
    // recent bot prompt sits at the top of the visible area. User reads top
    // to bottom and the sticky Done button (styled via CSS) remains visible.
    scrollPromptToTop();

    if (cfg.autoAdvance) {
      autoTimer = setTimeout(function () { cleanup(); onChoose(cfg.multi ? [] : null, true); }, AUTO_ADVANCE_MS);
    }
    function cleanup() { container.remove(); }
    function cancelAuto() { if (autoTimer) { clearTimeout(autoTimer); autoTimer = null; } }
  }

  function renderFreeText(placeholder, onSubmit) {
    var wrap = document.createElement('div');
    wrap.className = 'tss-msg tss-msg--user';
    wrap.style.maxWidth = '100%';
    var box = document.createElement('div');
    box.className = 'tss-freetext';
    var ta = document.createElement('textarea');
    ta.placeholder = placeholder; ta.maxLength = 500;
    box.appendChild(ta);
    var foot = document.createElement('div');
    foot.className = 'tss-freetext__footer';
    var counter = document.createElement('span');
    counter.textContent = '0 / 500';
    ta.addEventListener('input', function () { counter.textContent = ta.value.length + ' / 500'; });
    var submit = document.createElement('button');
    submit.className = 'tss-done-btn';
    submit.textContent = 'Send';
    submit.onclick = function () { clearTimeout(t); wrap.remove(); onSubmit(ta.value.trim(), false); };
    foot.appendChild(counter); foot.appendChild(submit);
    box.appendChild(foot); wrap.appendChild(box);

    var skip = document.createElement('button');
    skip.className = 'tss-skip-btn';
    skip.textContent = 'Skip this';
    skip.onclick = function () { clearTimeout(t); wrap.remove(); onSubmit('', true); };
    wrap.appendChild(skip);

    ui.body.appendChild(wrap);
    scrollBottom();
    setTimeout(function(){ ta.focus(); }, 120);
    var t = setTimeout(function () { wrap.remove(); onSubmit('', true); }, AUTO_ADVANCE_MS * 3);
  }

  function renderContact(q, onSubmit) {
    var wrap = document.createElement('div');
    wrap.className = 'tss-msg tss-msg--user';
    wrap.style.maxWidth = '100%';
    var box = document.createElement('div');
    box.className = 'tss-freetext';

    // Phone row with fixed +91 prefix (country code is pre-filled so user only enters 10 digits)
    var phoneRow = document.createElement('div');
    phoneRow.style.cssText = 'display:flex; align-items:center; gap:8px; padding:4px 0;';
    var prefix = document.createElement('span');
    prefix.textContent = '+91';
    prefix.style.cssText = 'font-family:var(--tss-font); font-size:14px; color:var(--tss-black); padding-right:8px; border-right:1px solid #e6e6e6; user-select:none;';
    var phoneEl = document.createElement('input');
    phoneEl.type = 'tel'; phoneEl.inputMode = 'numeric';
    phoneEl.placeholder = (q.phone_placeholder || 'Your WhatsApp number') + ' *';
    phoneEl.required = true;
    phoneEl.maxLength = 10;
    phoneEl.style.cssText = 'border:none; outline:none; flex:1; font-family:var(--tss-font); font-size:14px; padding:0;';
    phoneRow.appendChild(prefix); phoneRow.appendChild(phoneEl);

    var emailEl = document.createElement('input');
    emailEl.type = 'email';
    emailEl.placeholder = q.email_placeholder + ' (optional)';
    emailEl.style.cssText = 'border:none; outline:none; width:100%; font-family:var(--tss-font); font-size:14px; padding:4px 0; border-top:1px solid #eee; margin-top:6px;';
    box.appendChild(phoneRow); box.appendChild(emailEl);

    var errorEl = document.createElement('div');
    errorEl.style.cssText = 'color:#c93434; font-size:12px; margin-top:6px; display:none; font-family:var(--tss-font);';
    box.appendChild(errorEl);

    var submit = document.createElement('button');
    submit.className = 'tss-done-btn';
    submit.textContent = q.submit;
    submit.disabled = true;
    submit.style.opacity = '0.5';
    submit.style.cursor = 'not-allowed';

    function cleanDigits(v) { return (v || '').replace(/\D/g, '').slice(0, 10); }
    function validatePhone(v) { return cleanDigits(v).length === 10; }
    function updateSubmit() {
      var ok = validatePhone(phoneEl.value);
      submit.disabled = !ok;
      submit.style.opacity = ok ? '1' : '0.5';
      submit.style.cursor = ok ? 'pointer' : 'not-allowed';
      if (ok) errorEl.style.display = 'none';
    }
    // Strip non-digits live so +91, spaces, etc. can never end up inside the input
    phoneEl.addEventListener('input', function () {
      var cleaned = cleanDigits(phoneEl.value);
      if (phoneEl.value !== cleaned) phoneEl.value = cleaned;
      updateSubmit();
    });

    submit.onclick = function () {
      if (!validatePhone(phoneEl.value)) {
        errorEl.textContent = 'Please enter a valid 10-digit WhatsApp number.';
        errorEl.style.display = 'block';
        phoneEl.focus();
        return;
      }
      wrap.remove();
      // Always store with +91 prefix so downstream consumers (Bik, CRM) get a fully-qualified number
      onSubmit('+91' + cleanDigits(phoneEl.value), emailEl.value.trim(), false);
    };
    box.appendChild(submit);
    wrap.appendChild(box);

    ui.body.appendChild(wrap);
    scrollBottom();
    setTimeout(function(){ phoneEl.focus(); }, 120);
  }

  function showHelper(helper) {
    var overlay = document.createElement('div');
    overlay.className = 'tss-helper';
    var panel = document.createElement('div');
    panel.className = 'tss-helper__panel';
    panel.innerHTML = '<h4>' + esc(helper.title) + '</h4>' + helper.body.map(function(b){ return '<p>' + b + '</p>'; }).join('');
    var btn = document.createElement('button');
    btn.className = 'tss-helper__close';
    btn.textContent = 'Got it';
    btn.onclick = function () { overlay.remove(); };
    panel.appendChild(btn);
    overlay.appendChild(panel);
    overlay.onclick = function (e) { if (e.target === overlay) overlay.remove(); };
    ui.chat.appendChild(overlay);
  }

  function ackRandom(q, val, done) {
    var variants = (C.acknowledgments[q] && C.acknowledgments[q][val]) || null;
    if (!variants || !variants.length) return done();
    var msg = variants[Math.floor(Math.random() * variants.length)];
    botSay(msg, done);
  }

  function labelFor(opts, val) {
    var found = opts.find(function(o){ return o.value === val; });
    return found ? found.label : val;
  }

  function scrollBottom() { if (ui.body) ui.body.scrollTop = ui.body.scrollHeight; }

  // Align the most recent bot prompt with the top of the chat body so the user
  // sees the question first, then options, then the sticky Done button.
  function scrollPromptToTop() {
    if (!ui.body) return;
    var botMsgs = ui.body.querySelectorAll('.tss-msg--bot');
    var last = botMsgs[botMsgs.length - 1];
    if (!last) { scrollBottom(); return; }
    var bodyRect = ui.body.getBoundingClientRect();
    var msgRect = last.getBoundingClientRect();
    var delta = msgRect.top - bodyRect.top - 8; // small gap from the top
    ui.body.scrollTop = ui.body.scrollTop + delta;
  }

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }

  // ---- Boot ---------------------------------------------------------------
  function boot() {
    ui.chat        = document.getElementById('tss-quiz-chat');
    ui.close       = document.getElementById('tss-quiz-close');
    ui.body        = document.getElementById('tss-quiz-body');
    ui.stepLabel   = document.getElementById('tss-quiz-step');
    ui.progressBar = document.getElementById('tss-quiz-progress-bar');
    if (!ui.chat) return;

    ui.close.addEventListener('click', close);
    document.addEventListener('click', function (e) {
      var el = e.target.closest ? e.target.closest('[data-tss-quiz-open]') : null;
      if (el) { e.preventDefault(); open(el.getAttribute('data-tss-quiz-open') || 'custom'); }
    });

    // URL-based trigger so the quiz can be linked from the Shopify nav / sidebar.
    // Any of these will open the quiz on page load:
    //   • hash:  /#tss-quiz   (preferred for nav menus, no page reload on same domain)
    //   • query: /?openquiz=1
    // Source string is reported in the `start` event for analytics segmentation.
    try {
      var hash = (window.location.hash || '').toLowerCase();
      var qs   = (window.location.search || '').toLowerCase();
      if (hash === '#tss-quiz' || hash === '#take-quiz' || /[?&]openquiz=1/.test(qs)) {
        // Defer one tick so the chat panel is in the DOM and CSS is applied.
        setTimeout(function () { open('url'); }, 0);
      }
    } catch (e) {}

    // Handle same-page hash changes (e.g. a button with href="#tss-quiz" on
    // the homepage — no page reload, quiz opens instantly).
    window.addEventListener('hashchange', function () {
      try {
        var h = (window.location.hash || '').toLowerCase();
        if (h === '#tss-quiz' || h === '#take-quiz') open('url');
      } catch (e) {}
    });

    window.TSSQuiz = {
      open: open, close: close,
      getState: function(){ return state; },
      reset: function(){
        state = freshState(); save();
        try{ document.cookie = COMPLETE_COOKIE + '=; Max-Age=0; path=/'; } catch(e){}
        try{ if (window.TSSQuizVision) window.TSSQuizVision.clearThumbs(); } catch(e){}
      },
      computeFromState: function(){ return window.TSSQuizEngine.compute(state.answers); },
      // Used by the floater to know what state to render
      isComplete: function(){ return !!state.completed; },
      isInProgress: function(){ return !!state.started_at && !state.completed; }
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
